/**
 * How a fee line reads on the Fees screens (QA, 31 Oct 2026).
 *
 * Monthly invoices are named by their place in the academic year - "Monthly Fee - 2026 -
 * Month 10" - which is the 10th instalment, not October. The due date is the real month,
 * so "Month N" is shown as that month: "Monthly Fee - May 2027". Anything the server adds
 * after " · " (a sibling discount) is a note for its own line, not part of the name.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

export interface FeeLineLabel {
  title: string;
  /** Extra facts about the line, e.g. "Sibling discount". */
  notes: string[];
}

/** "May 2027" from a due date (YYYY-MM-DD or ISO), read as written so no time zone moves it. */
export function dueMonthName(dueDate: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})/.exec(dueDate ?? '');
  if (!m) return null;
  const month = Number(m[2]);
  return month >= 1 && month <= 12 ? `${MONTHS[month - 1]} ${m[1]}` : null;
}

export function feeLineLabel(name: string | null | undefined, dueDate?: string | null): FeeLineLabel {
  const [base, ...rest] = (name ?? '').split(' · ').map(p => p.trim());
  const notes = rest.filter(Boolean);
  const month = dueMonthName(dueDate);
  // "Monthly Fee - 2026 - Month 10" or "Monthly Fee - 2026-2027 - Month 10": the year and
  // the instalment number both give way to the month it is due.
  const title = month && /month\s*\d+/i.test(base)
    ? base.replace(/(\s*-\s*\d{4}(\s*-\s*\d{4})?)?\s*-?\s*month\s*\d+\b/i, ` - ${month}`).replace(/^\s*-\s*/, '').trim()
    : base;
  return { title: title || base || 'Fee', notes };
}

/** The label on one line, for pickers, receipts and screen readers. */
export const feeLineText = (name: string | null | undefined, dueDate?: string | null) => {
  const { title, notes } = feeLineLabel(name, dueDate);
  return [title, ...notes].join(' · ');
};
